import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Page from '../index'
import Tabs from '../../features/components/Tabs'
import CampaignsSection from './СampaignsSection'
import CampaignsActions from './CampaignsActions'
import PageSectionHeader from '../../features/components/PageSectionHeader'

import { getAvailablePlatformList } from '../../helpers/handlePlatformSelection'
import { useDeleteCampaignAction } from '../../features/hooks/useDeleteAction/useDeleteCampaignAction'

import { setSelectedPlatform } from '../../modules/actions/app'
import { clearCampaigns, clearCampaignsGraphs } from '../../modules/actions/campaigns'

import {
  selectedDV360AdAccountSelector,
  selectedFacebookAdAccountSelector,
  selectedGoogleAdAccountSelector,
  selectedTikTokAdAccountSelector
} from '../../modules/selectors/adAccounts'
import { selectedPlatformSelector } from '../../modules/selectors/app'
import { selectedPartnershipSelector } from '../../modules/selectors/partnerships'

import { ROUTES } from '../../constants/routes'
import {
  DV_360_PLATFORM,
  FACEBOOK_PLATFORM,
  GOOGLE_PLATFORM,
  TIKTOK_PLATFORM
} from '../../constants/selectLists/platformList'

import useStyles from './styles'

function CampaignsPage() {
  const classes = useStyles()
  const dispatch = useDispatch()

  const selectedPlatform = useSelector(selectedPlatformSelector)
  const { id: facebookAdAccountId } = useSelector(selectedFacebookAdAccountSelector)
  const { id: googleAdAccountId } = useSelector(selectedGoogleAdAccountSelector)
  const { id: dv360AdAccountId } = useSelector(selectedDV360AdAccountSelector)
  const { id: tikTokAdAccountId } = useSelector(selectedTikTokAdAccountSelector)
  const { providers: availableProviders } = useSelector(selectedPartnershipSelector)

  const [campaignsCheckboxes, setCampaignsCheckboxes] = useState({})

  const selectedCampaigns = useMemo(
    () => Object.keys(campaignsCheckboxes).filter(key => campaignsCheckboxes[key]),
    [campaignsCheckboxes]
  )

  const setSelectedCampaignsHandler = useCallback(updatedSelectedCampaigns => {
    setCampaignsCheckboxes(updatedSelectedCampaigns)
  }, [])

  const showSection = useMemo(
    () => ({
      [FACEBOOK_PLATFORM]: !!facebookAdAccountId && selectedPlatform === FACEBOOK_PLATFORM,
      [GOOGLE_PLATFORM]: !!googleAdAccountId && selectedPlatform === GOOGLE_PLATFORM,
      [DV_360_PLATFORM]: !!dv360AdAccountId && selectedPlatform === DV_360_PLATFORM,
      [TIKTOK_PLATFORM]: !!tikTokAdAccountId && selectedPlatform === TIKTOK_PLATFORM
    }),
    [selectedPlatform, facebookAdAccountId, googleAdAccountId, dv360AdAccountId, tikTokAdAccountId]
  )

  // don't show platform which user don't have access to
  const availablePlatformList = useMemo(() => getAvailablePlatformList(availableProviders), [availableProviders])

  const PlatformTabs = useMemo(
    () => (
      <Tabs
        options={availablePlatformList}
        selectedValue={selectedPlatform}
        onSelectValue={value => dispatch(setSelectedPlatform(value))}
      />
    ),
    [dispatch, availablePlatformList, selectedPlatform]
  )

  const breadcrumbs = useMemo(
    () => [
      {
        title: 'Partnerships',
        url: ROUTES.partnerships
      },
      {
        title: 'Campaign Hub'
      }
    ],
    []
  )

  const { handleDeleteRequest, DeleteCampaignModal } = useDeleteCampaignAction()

  useEffect(
    () => () => {
      dispatch(clearCampaigns())
      dispatch(clearCampaignsGraphs())
    },
    [dispatch]
  )

  return (
    <Page helmetTitle="Campaigns" breadcrumbs={breadcrumbs} className={classes.page}>
      <PageSectionHeader
        title="List of Campaigns"
        // show platform tabs selector when more then 1 platform available
        LeftPanel={availablePlatformList.length > 1 && PlatformTabs}
        RightPanel={
          selectedPlatform && (
            <CampaignsActions selectedPlatform={selectedPlatform} selectedCampaigns={selectedCampaigns} />
          )
        }
      />
      <CampaignsSection
        showSection={showSection}
        onRowCheck={setSelectedCampaignsHandler}
        handleDeleteRequest={handleDeleteRequest}
      />
      {DeleteCampaignModal}
    </Page>
  )
}

export default CampaignsPage
